import React, { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Toggle from '../../../components/common/toggleButton';
import InputField from '../../../components/common/input-field';
import { getNumberPattern } from '../../utils/helper';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import CustomTooltip from '../../../components/common/tooltip';
import { FieldLabel } from '@measured/puck';

export interface Padding {
  paddingTop: {
    desktop: string;
    mobile: string;
    tablet: string;
  };
  paddingBottom: {
    desktop: string;
    mobile: string;
    tablet: string;
  };
  paddingLeft: {
    desktop: string;
    mobile: string;
    tablet: string;
  };
  paddingRight: {
    desktop: string;
    mobile: string;
    tablet: string;
  };
}

interface Width {
  desktop: string;
  mobile: string;
  tablet: string;
}

const options = [
  { label: 'Off', value: false },
  { label: 'Auto', value: true },
];

export interface ColumnProps {
  value: IColumnsProps;
  onChange: (_value: IColumnsProps) => void;
  title: string;
}

export const columnProps: IColumnsProps = {
  deviceType: 'desktop',
  padding: {
    paddingTop: {
      desktop: '0',
      mobile: '0',
      tablet: '0',
    },
    paddingBottom: {
      desktop: '0',
      mobile: '0',
      tablet: '0',
    },
    paddingLeft: {
      desktop: '0',
      mobile: '0',
      tablet: '0',
    },
    paddingRight: {
      desktop: '0',
      mobile: '0',
      tablet: '0',
    },
  },
  width: {
    desktop: '80%',
    tablet: '80%',
    mobile: '90%',
  },
  marginAuto: {
    desktop: false,
    mobile: true,
    tablet: true,
  },
};

export interface IColumnsProps {
  deviceType: 'desktop' | 'mobile' | 'tablet';
  padding: Padding;
  width: Width;
  marginAuto: {
    desktop: boolean;
    mobile: boolean;
    tablet: boolean;
  };
}

const Column: React.FC<ColumnProps> = ({ title, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div>
      <div
        className="flex justify-between font-bold cursor-pointer py-2 bg-slate-100 rounded-md pagebuilder-theme"
        onClick={() => setIsOpen(!isOpen)}>
        <h3>{title}</h3>
        {!isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
      </div>
      {isOpen && (
        <div className="flex flex-col gap-4 mt-3  border border-gray-300 rounded p-2 w-full">
          <Toggle<IColumnsProps>
            value={value}
            onChange={newValue => onChange({ ...value, ...newValue })}
            heading={{ label: 'Device', value: 'device' }}
            options={[
              { label: 'Desktop', value: 'desktop' },
              { label: 'Tablet', value: 'tablet' },
              { label: 'Mobile', value: 'mobile' },
            ]}
            propName="deviceType"
          />
          <div className="grid grid-cols-[1fr_1fr] gap-2.5 border-t-1">
            <InputField
              value={value.padding.paddingTop?.[value?.deviceType]}
              onChange={e => {
                const newValue = getNumberPattern(e);
                if (newValue) {
                  onChange({
                    ...value,
                    padding: {
                      ...value.padding,
                      paddingTop: {
                        ...value.padding.paddingTop,
                        [value.deviceType]: newValue,
                      },
                    },
                  });
                }
              }}
              className="h-[42px]"
              label="Top"
            />
            <InputField
              value={value.padding.paddingBottom?.[value?.deviceType]}
              onChange={e => {
                const newValue = getNumberPattern(e);
                if (newValue) {
                  onChange({
                    ...value,
                    padding: {
                      ...value.padding,
                      paddingBottom: {
                        ...value.padding.paddingBottom,
                        [value.deviceType]: newValue,
                      },
                    },
                  });
                }
              }}
              className="h-[42px]"
              label="Bottom"
            />
            <InputField
              value={value.padding.paddingLeft?.[value?.deviceType]}
              onChange={e => {
                const newValue = getNumberPattern(e);
                if (newValue) {
                  onChange({
                    ...value,
                    padding: {
                      ...value.padding,
                      paddingLeft: {
                        ...value.padding.paddingLeft,
                        [value.deviceType]: newValue,
                      },
                    },
                  });
                }
              }}
              className="h-[42px]"
              label="Left"
            />
            <InputField
              value={value.padding.paddingRight?.[value?.deviceType]}
              onChange={e => {
                const newValue = getNumberPattern(e);
                if (newValue) {
                  onChange({
                    ...value,
                    padding: {
                      ...value.padding,
                      paddingRight: {
                        ...value.padding.paddingRight,
                        [value.deviceType]: newValue,
                      },
                    },
                  });
                }
              }}
              className="h-[42px]"
              label="Right"
            />
          </div>
          <FieldLabel
            label={
              (
                <CustomTooltip title="Width">
                  <span>Width</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }>
            <InputField
              value={value?.width?.[value.deviceType]}
              className="h-[42px]"
              onChange={e => {
                const newValue = e.replace(/[^0-9%]/g, '');
                onChange({
                  ...value,
                  width: {
                    ...value.width,
                    [value.deviceType]: newValue,
                  },
                });
              }}
            />
          </FieldLabel>
          <FieldLabel
            label={
              (
                <CustomTooltip title="device">
                  <span>Margin</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }
            el="div">
            <ToggleButtonGroup
              value={value.marginAuto?.[value.deviceType]}
              exclusive
              className="w-full"
              onChange={(_e, selectValue) =>
                onChange({
                  ...value,
                  marginAuto: {
                    ...value.marginAuto,
                    [value.deviceType]: selectValue,
                  },
                })
              }
              aria-label="margin">
              {options.map((option, index: number) => (
                <ToggleButton
                  key={index}
                  size="small"
                  value={option.value}
                  disableRipple
                  aria-label="margin"
                  className="flex-1">
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </FieldLabel>
        </div>
      )}
    </div>
  );
};

export default Column;
